import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Slider,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  PrivacyTip as PrivacyIcon,
  Devices as DevicesIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '../../store/slices/authSlice.js'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'

const EmployeeSettings = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()
  const [selectedTab, setSelectedTab] = useState(0)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false)

  const { orgId } = useSelector((state) => state.auth)

  const [settings, setSettings] = useState({
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      smsNotifications: false,
      leaveApprovalNotifications: true,
      attendanceReminders: true,
      salaryCreditedAlerts: true,
      holidayAlerts: true,
      teamUpdates: true,
      marketingEmails: false,
    },
    preferences: {
      language: 'english',
      timezone: 'UTC-05:00',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12-hour',
      theme: 'light',
      fontSize: 'medium',
      dashboardLayout: 'standard',
      defaultView: 'dashboard',
    },
    security: {
      twoFactorAuth: false,
      loginAlerts: true,
      deviceLogout: true,
      sessionTimeout: 30,
      passwordExpiryDays: 90,
      lastPasswordChange: '2024-01-01',
      failedLoginAttempts: 0,
    },
    privacy: {
      profileVisibility: 'team',
      showContactInfo: true,
      showBirthday: false,
      showAnniversary: true,
      dataSharing: 'limited',
      activityTracking: true,
      cookieConsent: true,
    },
    devices: [
      { id: 1, name: 'MacBook Pro', type: 'laptop', lastActive: '2024-01-15 14:30', status: 'active' },
      { id: 2, name: 'iPhone 13', type: 'mobile', lastActive: '2024-01-15 10:15', status: 'active' },
      { id: 3, name: 'iPad Air', type: 'tablet', lastActive: '2024-01-10 09:45', status: 'inactive' },
    ],
    sessions: [
      { id: 1, device: 'Chrome on Windows', location: 'New York, US', ip: '192.168.1.1', lastActive: 'Just now', active: true },
      { id: 2, device: 'Safari on iPhone', location: 'New York, US', ip: '192.168.1.2', lastActive: '2 hours ago', active: true },
      { id: 3, device: 'Firefox on Mac', location: 'Chicago, US', ip: '192.168.1.3', lastActive: '1 day ago', active: false },
    ],
  })

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    withLoading(async () => {
      await new Promise(resolve => setTimeout(resolve, 800))
      // In real app, fetch settings from API
    })
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
  }

  const handleNotificationToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [setting]: !prev.notifications[setting]
      }
    }))
  }

  const handlePreferenceChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [field]: value
      }
    }))
  }

  const handleSecurityToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      security: {
        ...prev.security,
        [setting]: !prev.security[setting]
      }
    }))
  }

  const handleSaveSettings = async () => {
    withLoading(async () => {
      await new Promise(resolve => setTimeout(resolve, 1000))
      // In real app, save settings via API
      toast.success('Settings saved successfully!')
    })
  }

  const handleAccountDelete = async () => {
    withLoading(async () => {
      await new Promise(resolve => setTimeout(resolve, 1500))
      // In real app, delete account via API
      dispatch(logout())
      navigate('/login')
      toast.success('Account deleted successfully')
      setOpenDeleteDialog(false)
    })
  }



  const tabs = [
    { label: 'Notifications', icon: <NotificationsIcon /> },
    { label: 'Preferences', icon: <PaletteIcon /> },
    { label: 'Security', icon: <SecurityIcon /> },
    { label: 'Privacy', icon: <PrivacyIcon /> },
    { label: 'Devices', icon: <DevicesIcon /> },
  ]

  const themes = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'auto', label: 'Auto (System)' },
  ]

  if (loading && !settings.preferences) {
    // Initial load
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading settings...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Settings
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Customize your experience and manage account settings
              </Typography>
            </Box>
          </Box>

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                />
              ))}
            </Tabs>
          </Paper>
        </Box>

        <Grid >
          <Grid size={{ xs: 12, md: 8 }}>
            {selectedTab === 0 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotificationsIcon />
                  Notification Settings
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.emailNotifications}
                      onChange={() => handleNotificationToggle('emailNotifications')}
                    />
                  }
                  label="Email Notifications"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.pushNotifications}
                      onChange={() => handleNotificationToggle('pushNotifications')}
                    />
                  }
                  label="Push Notifications"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.smsNotifications}
                      onChange={() => handleNotificationToggle('smsNotifications')}
                    />
                  }
                  label="SMS Notifications"
                />
              </Paper>
            )}

            {selectedTab === 1 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaletteIcon />
                  Preferences
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Theme</InputLabel>
                      <Select
                        value={settings.preferences.theme}
                        label="Theme"
                        onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                      >
                        {themes.map((theme) => (
                          <MenuItem key={theme.value} value={theme.value}>
                            {theme.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={settings.preferences.language}
                        label="Language"
                        onChange={(e) => handlePreferenceChange('language', e.target.value)}
                      >
                        <MenuItem value="english">English</MenuItem>
                        <MenuItem value="spanish">Spanish</MenuItem>
                        <MenuItem value="french">French</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Date Format</InputLabel>
                      <Select
                        value={settings.preferences.dateFormat}
                        label="Date Format"
                        onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                      >
                        <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                        <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                        <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {selectedTab === 2 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon />
                  Security Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.security.twoFactorAuth}
                        onChange={() => handleSecurityToggle('twoFactorAuth')}
                      />
                    }
                    label="Two-Factor Authentication"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.security.loginAlerts}
                        onChange={() => handleSecurityToggle('loginAlerts')}
                      />
                    }
                    label="Login Alerts"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.security.deviceLogout}
                        onChange={() => handleSecurityToggle('deviceLogout')}
                      />
                    }
                    label="Automatic Device Logout"
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Change Password</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/change-password')}
                    >
                      Update Password
                    </Button>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>Danger Zone</Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setOpenDeleteDialog(true)}
                    >
                      Delete Account
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}

            {selectedTab === 3 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PrivacyIcon />
                  Privacy Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Profile Visibility</InputLabel>
                    <Select
                      value={settings.privacy.profileVisibility}
                      label="Profile Visibility"
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        privacy: { ...prev.privacy, profileVisibility: e.target.value }
                      }))}
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="team">Team Only</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.privacy.showContactInfo}
                        onChange={() => setSettings(prev => ({
                          ...prev,
                          privacy: { ...prev.privacy, showContactInfo: !prev.privacy.showContactInfo }
                        }))}
                      />
                    }
                    label="Show Contact Info"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.privacy.activityTracking}
                        onChange={() => setSettings(prev => ({
                          ...prev,
                          privacy: { ...prev.privacy, activityTracking: !prev.privacy.activityTracking }
                        }))}
                      />
                    }
                    label="Allow Activity Tracking"
                  />
                </Box>
              </Paper>
            )}

            {selectedTab === 4 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DevicesIcon />
                  Active Devices
                </Typography>
                {settings.devices.map(device => (
                  <Box key={device.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {device.type === 'mobile' && <SmartphoneIcon />}
                      {device.type === 'tablet' && <TabletIcon />}
                      {device.type === 'laptop' && <ComputerIcon />}
                      <Box>
                        <Typography variant="body1">{device.name}</Typography>
                        <Typography variant="caption" color="textSecondary">Last active: {device.lastActive}</Typography>
                      </Box>
                    </Box>
                    <StatusChip status={device.status} />
                  </Box>
                ))}
              </Paper>
            )}

          </Grid>
        </Grid>
      </Paper>

      {/* Change Password Dialog */}
    </Box>
  )
}

export default EmployeeSettings